import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, fetchEmailFailures, retryEmailFailure } from '@/api/client'
import { EmptyState } from '@/components/EmptyState'
import { LoadingState } from '@/components/LoadingState'
import { formatDateTime } from '@/lib/format-date'

import { MailWarning } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

export function EmailFailuresAdminPage() {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const failuresQuery = useQuery({
    queryKey: ['finance', 'email-failures'],
    queryFn: fetchEmailFailures,
  })

  const retryMut = useMutation({
    mutationFn: retryEmailFailure,
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['finance', 'email-failures'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Retry failed')
    },
  })

  const rows = failuresQuery.data ?? []

  return (
    <AdminSection
      title="Email failures"
      tone="rose"
      icon={MailWarning}
      lede="Failed outbound deliveries (BR-34). Retry re-sends via the configured sender stub."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {failuresQuery.isLoading && (
        <LoadingState label="Loading failures…" variant="skeleton-table" rows={6} columns={6} />
      )}

      {!failuresQuery.isLoading && rows.length === 0 && (
        <EmptyState title="No failed deliveries recorded." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--dense">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Recipient</th>
                <th scope="col">Subject</th>
                <th scope="col">Status</th>
                <th scope="col">Error</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>{row.recipient}</td>
                  <td>{row.subject}</td>
                  <td>
                    <span className="badge badge--role badge--role-admin">{row.status}</span>
                  </td>
                  <td>{row.last_error ?? '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={retryMut.isPending}
                      onClick={() => retryMut.mutate(row.id)}
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  )
}
